import { SECURITY_EVENT_TYPE_OPTIONS } from '@dealstack/frontend/app/pages/Project/Cap/CapEntityManageFlow/common/utils';
import {
	CREATE_SECURITY_EVENT_FORM_FIELD,
	useSecurityEventDrawerController,
} from '@dealstack/frontend/app/pages/Project/Cap/CapEntityManageFlow/tabs/Securities/SecurityEventDrawer/useSecurityEventDrawerController';
import { SECURITY_NOMINAL_VALUE_PRECISION } from '@dealstack/frontend/shared/cap';
import { Icon } from '@dealstack/frontend/shared/components/fundament/Icon/Icon';
import { InlineLoader } from '@dealstack/frontend/shared/components/loaders/InlineLoader/InlineLoader';
import { useTranslationCapManage } from '@dealstack/frontend/shared/mechanism/i18n/i18nHooks';
import { drawerTransitionProps } from '@dealstack/frontend/shared/mechanism/styleguide/GlobalStyling';
import { Button, Drawer, Text, Title } from '@mantine/core';
import { memo, useMemo } from 'react';
import { FormProvider, useWatch } from 'react-hook-form';
import { NumberInput, Select } from 'react-hook-form-mantine';
import styled from 'styled-components';
import { MantineDateInput } from '@dealstack/frontend/shared/components/mantineForm/MantineDateInput';

import { browserDateToMidnightUTC } from '@dealstack/frontend/shared/utils/utc.ts';
import { SecurityEventType } from '@dealstack/shared/lib/generated';
import { TEST_ELEMENT_IDS } from '@dealstack/shared/tests/testElementIds.ts';

interface Props {
	securityId?: string;
	entityId?: string;
	className?: string;
	opened: boolean;
	onClose: (createdTransactionId?: string) => void;
	refreshSecuritiesList: () => void;
	refreshSecurityNominalValue: () => void;
}

const CreateSecurityEventDrawer = ({
	securityId,
	entityId,
	opened,
	onClose,
	className,
	refreshSecuritiesList,
	refreshSecurityNominalValue,
}: Props) => {
	const tCapManage = useTranslationCapManage();

	const {
		createSecurityEventRequest,
		createSecurityEventForm,
		onSubmit,
		availableSecurities,
		getConversionPlaceholder,
		handleSecurityTypeChange,
	} = useSecurityEventDrawerController(
		refreshSecuritiesList,
		refreshSecurityNominalValue,
		onClose,
		securityId,
		entityId
	);

	const selectedType = useWatch({
		control: createSecurityEventForm.control,
		name: CREATE_SECURITY_EVENT_FORM_FIELD.type,
	});
	const selectedDateIso = useWatch({
		control: createSecurityEventForm.control,
		name: CREATE_SECURITY_EVENT_FORM_FIELD.date,
	});

	const isPastEvent = useMemo(() => {
		if (!selectedDateIso) {
			return false;
		}
		const selectedDate = new Date(selectedDateIso);
		return selectedDate.getTime() < new Date().getTime();
	}, [selectedDateIso]);

	const splitOneTranslationKey = isPastEvent
		? 'securities.details.split_one'
		: 'securities.details.split_one_future';

	const reverseSplitTranslationKey = isPastEvent
		? 'securities.details.reverse_split'
		: 'securities.details.reverse_split_future';

	const conversionPlaceholder = getConversionPlaceholder();

	return (
		<Drawer
			opened={opened}
			onClose={onClose}
			position="right"
			className={className}
			transitionProps={drawerTransitionProps}
			trapFocus={false}
			title={
				<Title order={4} fw={600}>
					{tCapManage('securities.details.create_event')}
				</Title>
			}
		>
			{createSecurityEventRequest.isPending && <InlineLoader />}
			<FormProvider {...createSecurityEventForm}>
				<form onSubmit={createSecurityEventForm.handleSubmit(onSubmit())}>
					<MantineDateInput
						name={CREATE_SECURITY_EVENT_FORM_FIELD.date}
						label={tCapManage('transactions.drawer.date.label')}
						data-testid={TEST_ELEMENT_IDS.CAP_EVENT_DATE_INPUT}
						size="md"
						onChange={(value) => {
							const isoDate = browserDateToMidnightUTC(value);
							if (!isoDate) {
								return;
							}
							createSecurityEventForm.setValue(
								CREATE_SECURITY_EVENT_FORM_FIELD.date,
								isoDate
							);
						}}
					/>
					<Select
						name={CREATE_SECURITY_EVENT_FORM_FIELD.type}
						control={createSecurityEventForm.control}
						label={tCapManage('securities.details.event_type')}
						data-testid={TEST_ELEMENT_IDS.CAP_EVENT_TYPE_SELECT}
						placeholder={tCapManage('securities.drawer.type.placeholder')}
						size="md"
						rightSection={<Icon variant="caret-down" size="lg" />}
						rightSectionProps={{ style: { pointerEvents: 'none' } }}
						data={SECURITY_EVENT_TYPE_OPTIONS}
						onChange={(value: SecurityEventType) => {
							createSecurityEventForm.setValue(
								CREATE_SECURITY_EVENT_FORM_FIELD.type,
								value
							);
							handleSecurityTypeChange(value);
						}}
					/>
					{selectedType === SecurityEventType.SPLIT && (
						<div
							style={{
								display: 'flex',
								alignItems: 'center',
								gap: '0.5rem',
								width: '100%',
							}}
						>
							<span style={{ whiteSpace: 'nowrap' }}>
								{tCapManage(splitOneTranslationKey)}
							</span>
							<NumberInput
								data-testid={TEST_ELEMENT_IDS.CAP_EVENT_POST_UNITS_INPUT}
								name={CREATE_SECURITY_EVENT_FORM_FIELD.postEventUnits}
								control={createSecurityEventForm.control}
								size="md"
								hideControls={true}
								placeholder={tCapManage(
									'securities.drawer.votes_per_unit.placeholder'
								)}
								style={{ flexGrow: 1 }}
							/>
							<span style={{ whiteSpace: 'nowrap' }}>
								{tCapManage('securities.details.split_unit')}
							</span>
						</div>
					)}

					{selectedType === SecurityEventType.REVERSE_SPLIT && (
						<div
							style={{
								display: 'flex',
								alignItems: 'center',
								gap: '0.5rem',
								width: '100%',
							}}
						>
							<NumberInput
								data-testid={TEST_ELEMENT_IDS.CAP_EVENT_PRE_UNITS_INPUT}
								name={CREATE_SECURITY_EVENT_FORM_FIELD.preEventUnits}
								control={createSecurityEventForm.control}
								size="md"
								hideControls={true}
								placeholder={tCapManage(
									'securities.drawer.votes_per_unit.placeholder'
								)}
								style={{ flexGrow: 1 }}
							/>
							<span style={{ whiteSpace: 'nowrap' }}>
								{tCapManage(reverseSplitTranslationKey)}
							</span>
						</div>
					)}

					{selectedType === SecurityEventType.ADJUST && (
						<NumberInput
							name={CREATE_SECURITY_EVENT_FORM_FIELD.securityDetailsNominalValue}
							control={createSecurityEventForm.control}
							size="md"
							hideControls={true}
							label={tCapManage('securities.details.adjust_value')}
							placeholder={tCapManage(
								'securities.drawer.new_nominal_value.placeholder'
							)}
							decimalScale={SECURITY_NOMINAL_VALUE_PRECISION}
							style={{ width: '100%' }}
						/>
					)}

					{selectedType === SecurityEventType.CONVERSION && (
						<div
							style={{
								display: 'flex',
								flexDirection: 'column',
								gap: '0.5rem',
								width: '100%',
							}}
						>
							<Text size="B6Bold">{tCapManage('securities.details.conversion')}</Text>
							<Select
								data-testid={TEST_ELEMENT_IDS.CAP_EVENT_CONVERSION_SELECT}
								name={CREATE_SECURITY_EVENT_FORM_FIELD.postEventSecurityId}
								control={createSecurityEventForm.control}
								size="md"
								rightSection={<Icon variant="caret-down" size="lg" />}
								rightSectionProps={{ style: { pointerEvents: 'none' } }}
								data={availableSecurities}
								placeholder={conversionPlaceholder}
								disabled={availableSecurities.length === 0}
							/>
						</div>
					)}

					<Button
						fullWidth
						mt="auto"
						type="submit"
						data-testid={TEST_ELEMENT_IDS.CAP_EVENT_CREATE_BUTTON}
					>
						{tCapManage('securities.details.button_create_event')}
					</Button>
				</form>
			</FormProvider>
		</Drawer>
	);
};

const Memoized = memo(CreateSecurityEventDrawer);
const Styled = styled(Memoized)`
	form {
		height: 100%;
		display: flex;
		flex-direction: column;
		gap: 1rem;
	}
`;

export { Styled as CreateSecurityEventDrawer };
